/**
 * StaffAttendanceSummaryPage — all-staff admin attendance + leave-balance
 * summary for one month (Prompt 11E.6). Owner/admin/manager only
 * (route-gated at src/routes/index.tsx).
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { PageContainer } from '@/components/layout';
import { Card, CardHeader, CardTitle, Button, Table, Badge } from '@/components/common';
import {
  useStaffAttendanceSummary,
  useOverrideRequestQueue,
  useApproveOverrideRequest,
  useDenyOverrideRequest,
  type StaffAttendanceSummaryRow,
  type StaffAttendanceOverrideRequest,
  type OverrideRequestStatus,
} from '@features/attendance/api/staffAttendance';

const overrideStatusVariant = (
  status: OverrideRequestStatus,
): 'primary' | 'success' | 'error' | 'default' =>
  status === 'pending' ? 'primary' : status === 'approved' ? 'success' : 'error';

const OverrideRequestRow: React.FC<{ request: StaffAttendanceOverrideRequest }> = ({
  request,
}) => {
  const approve = useApproveOverrideRequest();
  const deny = useDenyOverrideRequest();
  const busy = approve.isPending || deny.isPending;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 0',
        borderBottom: '1px solid #e5e7eb',
      }}
      data-testid={`candeza-override-queue-row-${request.id}`}
    >
      <div>
        <strong>
          {request.staff_id} — {request.date}
        </strong>
        <div style={{ fontSize: 12, color: '#64748b' }}>
          Requests: {request.requested_status}
          {request.note && ` · "${request.note}"`}
        </div>
      </div>
      {request.status === 'pending' ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="sm"
            disabled={busy}
            onClick={() => approve.mutate({ id: request.id, body: {} })}
            data-testid={`candeza-override-queue-approve-${request.id}`}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={() => deny.mutate({ id: request.id, body: {} })}
            data-testid={`candeza-override-queue-deny-${request.id}`}
          >
            Deny
          </Button>
        </div>
      ) : (
        <Badge variant={overrideStatusVariant(request.status)} size="sm">
          {request.status}
        </Badge>
      )}
    </div>
  );
};

export const StaffAttendanceSummaryPage: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const { data, isLoading, isError } = useStaffAttendanceSummary(year, month);
  // Table<T> requires T extends {id: string} — the row's natural key is
  // staff_id, so alias it rather than changing the backend's field name.
  const rows = (data?.rows ?? []).map((row) => ({ ...row, id: row.staff_id }));

  const { data: overrideQueue, isLoading: overrideLoading } = useOverrideRequestQueue('pending');
  const overrideItems = overrideQueue?.items ?? [];

  const goPrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear((y) => y - 1);
    } else {
      setMonth((m) => m - 1);
    }
  };
  const goNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear((y) => y + 1);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString('en-IN', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <PageContainer
      title="Staff Attendance"
      subtitle="All-staff attendance + leave-balance summary"
      data-testid="candeza-staff-attendance-summary-page"
    >
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
        <Button variant="outline" size="sm" onClick={goPrevMonth}>
          ← Prev
        </Button>
        <strong>{monthLabel}</strong>
        <Button variant="outline" size="sm" onClick={goNextMonth}>
          Next →
        </Button>
      </div>

      <Card padding="lg">
        {isError ? (
          <p style={{ color: '#dc2626' }}>Could not load the attendance summary.</p>
        ) : (
          <Table<StaffAttendanceSummaryRow & { id: string }>
            data={rows}
            isLoading={isLoading}
            emptyMessage="No staff records this month."
            testId="candeza-staff-attendance-summary-table"
            getRowTestId={(row) => `candeza-staff-attendance-summary-row-${row.staff_id}`}
            columns={[
              {
                key: 'staff_name',
                header: 'Staff',
                isPrimary: true,
                render: (row) => <Link to={`/staff/${row.staff_id}`}>{row.staff_name}</Link>,
              },
              { key: 'present_days', header: 'Present days', render: (row) => row.present_days },
              { key: 'absent_days', header: 'Absent days', render: (row) => row.absent_days },
              {
                key: 'attendance_pct',
                header: 'Attendance %',
                render: (row) => (row.attendance_pct != null ? `${row.attendance_pct}%` : '—'),
              },
              {
                key: 'leave_balance',
                header: 'Leave balance',
                render: (row) => row.leave_balance ?? '—',
              },
            ]}
          />
        )}
      </Card>

      <Card padding="lg" style={{ marginTop: 24 }} data-testid="candeza-override-queue">
        <CardHeader>
          <CardTitle>Pending attendance override requests</CardTitle>
        </CardHeader>
        <div style={{ marginTop: 12 }}>
          {overrideLoading ? (
            <p style={{ color: '#64748b' }}>Loading…</p>
          ) : overrideItems.length === 0 ? (
            <p style={{ color: '#64748b' }}>No pending override requests.</p>
          ) : (
            overrideItems.map((req) => <OverrideRequestRow key={req.id} request={req} />)
          )}
        </div>
      </Card>
    </PageContainer>
  );
};

export default StaffAttendanceSummaryPage;
